import { EventType } from "@/analytics/analytics-events";
import { DeepLinkProperties } from "@/analytics/deep-link-events-properties";
import { useViewRuntimeContext } from "@/components/common/view-runtime-context";
import {
  createViewShareLink,
  type CaptureSavedViewOptions,
} from "@/features/saved-views/capture-saved-view";
import { logError } from "@/logging/logging";
import { selectModeName } from "@/store/mode-selectors";
import { useAppSelector, useAppStore } from "@/store/store-hooks";
import { selectProjectAccessLevel } from "@faro-lotv/app-component-toolbox";
import {
  Alert,
  FaroButton,
  Dialog,
  FaroText,
  TextField,
  TextFieldProps,
  useToast,
} from "@faro-lotv/flat-ui";
import { Analytics } from "@faro-lotv/foreign-observers";
import { assert } from "@faro-lotv/foundation";
import { selectProjectId } from "@faro-lotv/project-source";
import { ProjectAccessLevel } from "@faro-lotv/service-wires";
import { Stack } from "@mui/material";
import { useCallback, useRef, useState, type JSX } from "react";

/** What to share, and how to describe it to the user. */
export type ShareViewOptions = CaptureSavedViewOptions & {
  /** The sentence describing what the link points at. */
  description?: string;
};

const DEFAULT_DESCRIPTION = "Share a link to your current location.";

/** The share flow, split into the trigger and the dialog the caller renders. */
export type ShareViewDialogHandle = {
  /** Opens the dialog and starts creating the link for the given view. */
  openShareDialog(options: ShareViewOptions): void;

  /** The dialog to render; it stays closed until `openShareDialog` is called. */
  shareDialog: JSX.Element;
};

/**
 * Provides the single share flow every share entry point uses.
 *
 * The link is created when the dialog opens rather than when the user copies, so
 * it can be shown in the dialog the way it always has been. Creating it hits the
 * backend once the short-share feature is on, hence the pending and failure states
 * the dialog renders.
 *
 * @returns the trigger to open the dialog and the dialog element to render.
 */
export function useShareViewDialog(): ShareViewDialogHandle {
  const [isOpen, setIsOpen] = useState(false);
  const [options, setOptions] = useState<ShareViewOptions>();
  const [link, setLink] = useState<string>();
  const [hasFailed, setHasFailed] = useState(false);

  const store = useAppStore();
  const viewContext = useViewRuntimeContext();
  const projectId = useAppSelector(selectProjectId);

  // Identifies the newest link creation, so a link the user superseded by closing
  // or re-opening the dialog never lands in it.
  const latestRequest = useRef(0);

  const openShareDialog = useCallback(
    (options: ShareViewOptions) => {
      assert(projectId, "A project is required to share a view");

      const request = ++latestRequest.current;
      setIsOpen(true);
      setOptions(options);
      setLink(undefined);
      setHasFailed(false);

      createViewShareLink(projectId, store.getState(), viewContext, options)
        .then((url) => {
          if (latestRequest.current === request) setLink(url.toString());
        })
        .catch((error: unknown) => {
          logError(error);
          if (latestRequest.current === request) setHasFailed(true);
        });
    },
    [projectId, store, viewContext],
  );

  const closeShareDialog = useCallback(() => {
    latestRequest.current++;
    // Keeps the options, so the dialog's content doesn't change while it fades out
    setIsOpen(false);
  }, []);

  return {
    openShareDialog,
    shareDialog: (
      <ShareViewDialog
        open={isOpen}
        options={options}
        link={link}
        hasFailed={hasFailed}
        onClose={closeShareDialog}
      />
    ),
  };
}

type ShareViewDialogProps = {
  /** Whether the dialog is open. */
  open: boolean;

  /** The view being shared, undefined until the dialog was first opened. */
  options: ShareViewOptions | undefined;

  /** The link to copy, undefined while it is still being created. */
  link: string | undefined;

  /** Whether the link could not be created. */
  hasFailed: boolean;

  /** Called when the dialog should close, after a copy or a cancel. */
  onClose(): void;
};

/**
 * @returns the dialog showing the link to a view and letting the user copy it
 */
function ShareViewDialog({
  open,
  options,
  link,
  hasFailed,
  onClose,
}: ShareViewDialogProps): JSX.Element {
  const { openToast } = useToast();
  const modeName = useAppSelector(selectModeName);
  const projectAccessLevel = useAppSelector(selectProjectAccessLevel);

  const trackEvent = useCallback(
    (type: EventType) => {
      Analytics.track<DeepLinkProperties>(type, {
        mode: modeName,
        access: projectAccessLevel ?? ProjectAccessLevel.private,
      });
    },
    [modeName, projectAccessLevel],
  );

  const copyLinkAndCloseDialog = useCallback<CopyTextFieldProps["onCopyLink"]>(
    (ev) => {
      // If the user selected only a part of the url prevent the default copy logic
      // so we can save in the clipboard the entire url
      ev.preventDefault();
      if (!link) return;

      trackEvent(EventType.copyDeepLink);

      navigator.clipboard.writeText(link);
      onClose();

      openToast({ title: "Link copied" });
    },
    [link, onClose, openToast, trackEvent],
  );

  const closeLinkDialog = useCallback(() => {
    trackEvent(EventType.closeDeepLink);
    onClose();
  }, [onClose, trackEvent]);

  return (
    <Dialog
      title="Share direct link"
      isConfirmDisabled
      open={open}
      onClose={closeLinkDialog}
      showXButton
    >
      <Stack sx={{ gap: "0.625rem" }}>
        <FaroText variant="bodyM">
          {options?.description ?? DEFAULT_DESCRIPTION}
        </FaroText>
        {projectAccessLevel === ProjectAccessLevel.private && (
          <Alert
            variant="info"
            title="Users must have access to the project to view it. If the project is public, anyone on the Internet can view it."
          />
        )}
        {hasFailed ? (
          <Alert
            variant="error"
            title="The link could not be created. Please close this dialog and try again."
          />
        ) : (
          <CopyTextField
            onCopyLink={copyLinkAndCloseDialog}
            link={link}
            fullWidth
          />
        )}
      </Stack>
    </Dialog>
  );
}

type CopyTextFieldProps = TextFieldProps & {
  /** Callback called when the user want to copy the link, from the button or the text field */
  onCopyLink(event: React.MouseEvent | React.ClipboardEvent): void;

  /** The link to show to the user, undefined while it is still being created */
  link: string | undefined;
};

/**
 * @returns a text field with a button to copy its content
 */
function CopyTextField({
  onCopyLink,
  link,
  ...rest
}: CopyTextFieldProps): JSX.Element {
  return (
    <TextField
      {...rest}
      text={link ?? ""}
      label="Project link"
      onCopy={onCopyLink}
      onFocus={(ev) => {
        ev.target.select();
      }}
      readOnly
      endAdornment={
        <FaroButton
          variant="primary"
          size="m"
          onClick={onCopyLink}
          isLoading={!link}
          aria-label="copy link"
        >
          Copy
        </FaroButton>
      }
      sx={{
        padding: 0,
        height: "2.5rem",
        // Use && to have more priority over other mui classes
        "&& >input": {
          py: 0,
          height: "inherit",
          overflow: "hidden",
          textOverflow: "ellipsis",
        },
        // Make the button the same height of the TextField
        "& >button": {
          height: "inherit",
          // Border only on the right
          borderRadius: "0 4px 4px 0",
        },
      }}
    />
  );
}
